import { computed, nextTick, reactive, ref, watch } from 'vue';
import axios from 'axios';
import { confirmAction } from './useConfirm';
import { useToast } from './useToast';
import { notifyHttpError } from '../utils/httpErrors';
import { exportScreensExcel } from '../utils/exportScreensExcel';
import { buildDefaultFrequencies, isCampaignHourActive } from './rtbDefaultFrequency';
import { isFrameCompatibleWithCreative } from '../utils/rtbCreativeCoverage';

const moneyFormat = (value, digits = 2) => {
    const nf = new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'KZT',
        currencyDisplay: 'symbol',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
    if (value == null || value === '') return digits === 2 ? nf.format(0) : '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return digits === 2 ? nf.format(0) : '—';
    return nf.format(n);
};

/**
 * Ставки, частоты и матрица креатив×экран. Поведение совпадает с прежней Options API-формой.
 */
export const useRtbBidMatrix = ({
    props,
    frames,
    selectedCreativeIds,
    selectedFrameGids,
    campaignSchedule,
    audienceEnabled,
    audiencePreview,
    isEditMode,
    canWrite,
    isAdmin,
    resolveCreative,
    markDirty,
}) => {
    const toast = useToast();
    const exportingScreens = ref(false);
    const editMapReady = ref(!props.campaign?.id);
    const isHydratingEditState = ref(false);
    const applyingDayIso = ref(null);
    const frameSettingsMap = reactive({});
    const expandedCreatives = reactive({});
    const creativeBids = reactive({});
    const excludedPairs = reactive({});
    const openSchedules = reactive({});
    const frameFrequenciesMap = reactive({});
    const campaignFrequencies = ref([]);
    let frameFreqsStore = {};

    const frameRows = () => (frames ? frames.value : props.frames) || [];

    const framesByGid = computed(() => {
        const map = new Map();
        for (const frame of frameRows()) {
            map.set(String(frame.gid), frame);
        }
        return map;
    });

    const viewerMultiplier = computed(() => {
        const raw = props.campaign?.viewer_multiplier ?? props.userData?.viewer_multiplier ?? 1;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : 1;
    });

    const selectedCreativeIdsSorted = computed(() => [...selectedCreativeIds.value]
        .map(Number)
        .filter(id => !Number.isNaN(id))
        .sort((a, b) => b - a));

    const showFrameMap = computed(() => frameRows().length > 0 && (!isEditMode.value || editMapReady.value));

    const moneyColumns = computed(() => props.campaign?.money?.columns || []);

    const frameSettingsColCount = computed(() => {
        let n = 6;
        if (audienceEnabled.value) n += 1;
        if (isEditMode.value) n += 3;
        return n;
    });

    const pairKey = (creativeId, gid) => `${creativeId}:${gid}`;

    const getFrameSetting = (creativeId, frameGid, field) => frameSettingsMap[pairKey(creativeId, frameGid)]?.[field] ?? null;
    const getFrameStats = (creativeId, frameGid) => frameSettingsMap[pairKey(creativeId, frameGid)]?.stats ?? null;
    const isFrameInRotation = (creativeId, gid) => getFrameSetting(creativeId, gid, 'enabled') !== false;

    const ensurePair = (creativeId, gid) => {
        const key = pairKey(creativeId, gid);
        if (!frameSettingsMap[key]) frameSettingsMap[key] = {};
        return frameSettingsMap[key];
    };

    const setFrameEnabled = (creativeId, gid, enabled) => {
        ensurePair(creativeId, gid).enabled = !!enabled;
        if (enabled) delete excludedPairs[pairKey(creativeId, gid)];
        markDirty();
    };

    const setFrameSetting = (creativeId, frameGid, field, value) => {
        ensurePair(creativeId, frameGid)[field] = value !== '' ? Number(value) : null;
        markDirty();
    };

    const setFrameStringSetting = (creativeId, frameGid, field, value) => {
        const row = ensurePair(creativeId, frameGid);
        row[field] = value;
        if (field === 'bid_strategy' && value === 'auto') row.bid_price = null;
        markDirty();
    };

    const setCreativeBid = (creativeId, value) => {
        creativeBids[creativeId] = value !== '' ? Number(value) : null;
        markDirty();
    };

    const getCreativeLengthSeconds = (creativeId) => {
        const creative = resolveCreative(creativeId);
        return Number(creative?.length_seconds ?? creative?.duration ?? 0) || 0;
    };

    const buildDefaultFrequenciesForPair = () => buildDefaultFrequencies(campaignSchedule.value);

    const getFramesForCreative = (creativeId) => {
        if (isEditMode.value) {
            const gids = new Set();
            const prefix = `${creativeId}:`;
            for (const key of Object.keys(frameSettingsMap)) {
                if (!key.startsWith(prefix) || excludedPairs[key]) continue;
                gids.add(key.slice(prefix.length));
            }
            for (const gid of selectedFrameGids.value) {
                const key = pairKey(creativeId, gid);
                if (excludedPairs[key] || gids.has(String(gid))) continue;
                const creative = resolveCreative(creativeId);
                const frame = framesByGid.value.get(String(gid));
                if (isFrameCompatibleWithCreative(creative, frame)) gids.add(String(gid));
            }
            return [...gids];
        }

        return selectedFrameGids.value.filter((gid) => {
            if (excludedPairs[pairKey(creativeId, gid)]) return false;
            return isFrameCompatibleWithCreative(resolveCreative(creativeId), framesByGid.value.get(String(gid)));
        });
    };

    const canDeleteFrameBinding = (creativeId, gid) => Number(getFrameStats(creativeId, gid)?.impr_total ?? 0) <= 0;

    const removeFrameFromCreative = (creativeId, gid) => {
        if (!canDeleteFrameBinding(creativeId, gid)) {
            setFrameEnabled(creativeId, gid, false);
            return;
        }
        const key = pairKey(creativeId, gid);
        excludedPairs[key] = true;
        openSchedules[key] = false;
        markDirty();
    };

    const restoreExcludedPairsForSelection = () => {
        for (const creativeId of selectedCreativeIds.value) {
            for (const gid of selectedFrameGids.value) {
                delete excludedPairs[pairKey(creativeId, gid)];
            }
        }
    };

    const getCreativeName = (id) => resolveCreative(id)?.name || `Креатив #${id}`;

    const getCreativeStatsSummary = (creativeId) => {
        if (!isEditMode.value) return null;
        const primaryKey = props.campaign?.money?.primary_key;
        const gids = getFramesForCreative(creativeId);
        let impressions = 0;
        let imprDaily = 0;
        let primarySpent = 0;
        let lastShowAtKz = null;
        let hasStats = false;

        for (const gid of gids) {
            const stats = getFrameStats(creativeId, gid);
            if (!stats) continue;
            hasStats = true;
            impressions += Number(stats.impr_total) || 0;
            imprDaily += Number(stats.impr_daily) || 0;
            const columns = stats.columns || moneyColumns.value;
            const col = primaryKey ? columns.find(item => item.key === primaryKey) : columns[0];
            primarySpent += Number(col?.spent) || 0;
            const showAt = stats.last_show_at_kz ? String(stats.last_show_at_kz).replace('T', ' ').slice(0, 19) : null;
            if (showAt && (!lastShowAtKz || showAt > lastShowAtKz)) lastShowAtKz = showAt;
        }

        return hasStats ? { impressions, impr_daily: imprDaily, primary_spent: primarySpent, last_show_at_kz: lastShowAtKz } : null;
    };

    const creativeStatsSummaryById = computed(() => {
        const map = {};
        for (const creativeId of selectedCreativeIdsSorted.value) {
            map[creativeId] = getCreativeStatsSummary(creativeId);
        }
        return map;
    });

    const getFrameLabel = (gid) => {
        const frame = framesByGid.value.get(String(gid));
        if (!frame) return `Экран #${gid}`;
        const street1 = String(frame.street_1_name || frame.street_1 || '').trim();
        const street2 = String(frame.street_2_name || frame.street_2 || '').trim();
        const streetPart = [street1, street2].filter(Boolean).join(' — ');
        const extras = [frame.landmark, frame.construction_type_label || frame.construction_type]
            .map(value => String(value || '').trim())
            .filter((value, index, list) => value !== '' && list.indexOf(value) === index);
        const composed = [streetPart, extras.join(', ')].filter(Boolean).join(', ');
        if (composed) return composed;
        return frame.address || frame.city || `Экран #${gid}`;
    };

    const frameOtsLabel = (gid) => {
        const row = audiencePreview?.value?.by_gid?.[gid];
        if (!row || row.ots == null) return 'нет данных';
        return new Intl.NumberFormat('ru-RU').format(Math.round(Number(row.ots) || 0));
    };

    const formatLastShowAtKz = (value) => {
        if (!value) return '—';
        const normalized = String(value).replace('T', ' ').slice(0, 19);
        const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2})$/);
        if (!match) return normalized;
        const [, year, month, day, time] = match;
        return `${time}\n${day}.${month}.${year}`;
    };

    const formatLastShowAtKzCompact = (value) => {
        if (!value) return '—';
        const normalized = String(value).replace('T', ' ').slice(0, 19);
        const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2})$/);
        if (!match) return normalized;
        const [, year, month, day, time] = match;
        return `${day}.${month}.${year} ${time}`;
    };

    const toggleCreativeExpand = (creativeId) => {
        expandedCreatives[creativeId] = !expandedCreatives[creativeId];
    };

    const isScheduleOpen = (creativeId, gid) => Boolean(openSchedules[pairKey(creativeId, gid)]);
    const getSchedule = (creativeId, gid) => frameSettingsMap[pairKey(creativeId, gid)]?.schedule || [];
    const setSchedule = (creativeId, gid, schedule) => {
        ensurePair(creativeId, gid).schedule = schedule;
        markDirty();
    };

    const toggleSchedule = (creativeId, gid) => {
        const key = pairKey(creativeId, gid);
        if (openSchedules[key]) {
            openSchedules[key] = false;
            return;
        }
        for (const existing of Object.keys(openSchedules)) {
            if (existing !== key && openSchedules[existing]) openSchedules[existing] = false;
        }
        openSchedules[key] = true;
        if (!Array.isArray(frameFrequenciesMap[key]) || frameFrequenciesMap[key].length === 0) {
            if (Array.isArray(frameFreqsStore[key]) && frameFreqsStore[key].length > 0) {
                frameFrequenciesMap[key] = [...frameFreqsStore[key]];
            } else if (campaignFrequencies.value.length > 0) {
                frameFrequenciesMap[key] = campaignFrequencies.value
                    .filter(cell => isCampaignHourActive(campaignSchedule.value, Number(cell.day_of_week), Number(cell.hour)))
                    .map(cell => ({ ...cell }));
            } else {
                frameFrequenciesMap[key] = buildDefaultFrequenciesForPair();
            }
        }
        frameFreqsStore[key] = frameFrequenciesMap[key];
    };

    const onFrequenciesUpdated = (payload) => {
        if (!payload?.creative_id || !(payload.gid || payload.screen_id)) return;
        const gid = payload.gid !== undefined ? payload.gid : payload.screen_id;
        const key = pairKey(payload.creative_id, gid);
        frameFrequenciesMap[key] = payload.values || [];
        frameFreqsStore[key] = frameFrequenciesMap[key];
        markDirty();
    };

    const onCampaignFrequenciesUpdated = (payload) => {
        if (!payload || !Array.isArray(payload.values)) return;
        campaignFrequencies.value = payload.values;
        markDirty();
    };

    const buildCampaignFrequenciesPayload = () => {
        const source = campaignFrequencies.value.length > 0
            ? campaignFrequencies.value
            : buildDefaultFrequenciesForPair();
        return source
            .filter(cell => isCampaignHourActive(campaignSchedule.value, Number(cell.day_of_week), Number(cell.hour)))
            .map(cell => ({
                day_of_week: cell.day_of_week,
                hour: cell.hour,
                min_interval_ms: cell.min_interval_ms ?? null,
            }));
    };

    const buildFrameFrequenciesPayload = () => {
        const items = [];
        const keys = new Set([...Object.keys(frameFrequenciesMap), ...Object.keys(frameFreqsStore)]);
        for (const key of keys) {
            let values = frameFrequenciesMap[key];
            if (!Array.isArray(values) || values.length === 0) values = frameFreqsStore[key];
            if (!Array.isArray(values)) continue;
            const [creativeId, gid] = key.split(':');
            if (!creativeId || !gid) continue;
            items.push({
                creative_id: Number(creativeId),
                gid: String(gid),
                values: values.map(cell => ({
                    day_of_week: cell.day_of_week,
                    hour: cell.hour,
                    min_interval_ms: cell.min_interval_ms ?? null,
                })),
            });
        }
        return items;
    };

    const patchBindingFrequenciesForDay = (dayOfWeek, dayCells) => {
        const byHour = {};
        for (const cell of dayCells || []) byHour[Number(cell.hour)] = cell.min_interval_ms ?? null;
        const ensureKeys = new Set([...Object.keys(frameFrequenciesMap), ...Object.keys(frameFreqsStore)]);
        for (const creativeId of selectedCreativeIds.value) {
            for (const gid of getFramesForCreative(creativeId)) ensureKeys.add(pairKey(creativeId, gid));
        }
        const patchList = (existing) => {
            const list = Array.isArray(existing) && existing.length > 0 ? [...existing] : [...campaignFrequencies.value];
            const withoutDay = list.filter(cell => Number(cell.day_of_week) !== Number(dayOfWeek));
            for (const [hourStr, minIntervalMs] of Object.entries(byHour)) {
                withoutDay.push({
                    day_of_week: Number(dayOfWeek),
                    hour: Number(hourStr),
                    min_interval_ms: minIntervalMs,
                    max_per_minute: null,
                });
            }
            return withoutDay;
        };
        for (const key of ensureKeys) {
            const patched = patchList(frameFrequenciesMap[key] || frameFreqsStore[key] || null);
            frameFrequenciesMap[key] = patched;
            frameFreqsStore[key] = patched;
        }
    };

    const onApplyCampaignDay = async (payload) => {
        if (!canWrite.value || !isAdmin.value || !isEditMode.value || !props.campaign?.id) return;
        const dayName = payload?.day_name || '';
        const dayOfWeek = Number(payload?.day_of_week);
        const values = payload?.values || [];
        const ok = await confirmAction({
            title: `Применить частоты дня «${dayName}» ко всем связкам?`,
            message: 'Точечные значения этого дня будут перезаписаны.',
            confirmLabel: 'Применить',
        });
        if (!ok) return;
        applyingDayIso.value = dayOfWeek + 1;
        try {
            const response = await axios.post('/administration/rtb-frequency/apply-day', {
                campaign_id: props.campaign.id,
                day_of_week: dayOfWeek,
                values,
            });
            const templateDay = response.data?.template_day || values.map(item => ({
                day_of_week: dayOfWeek,
                hour: item.hour,
                min_interval_ms: item.min_interval_ms ?? null,
                max_per_minute: null,
            }));
            campaignFrequencies.value = [
                ...(campaignFrequencies.value || []).filter(cell => Number(cell.day_of_week) !== dayOfWeek),
                ...templateDay.map(cell => ({
                    day_of_week: Number(cell.day_of_week),
                    hour: Number(cell.hour),
                    min_interval_ms: cell.min_interval_ms ?? null,
                    max_per_minute: null,
                })),
            ];
            patchBindingFrequenciesForDay(dayOfWeek, templateDay);
            toast.success(`Частоты ${dayName} применены ко всем связкам`);
        } catch (error) {
            notifyHttpError(error, 'Не удалось применить частоты');
        } finally {
            applyingDayIso.value = null;
        }
    };

    const getAutoBidTooltip = (creativeId, gid) => {
        const current = getFrameSetting(creativeId, gid, 'auto_bid_current');
        if (current) {
            return `Текущая авто-ставка: ${moneyFormat(current, 4)}. Алгоритм автоматически корректирует ставку для оптимальной закупки.`;
        }
        return 'Авто-биддер определит оптимальную ставку после получения первых результатов аукционов.';
    };

    const buildFrameSettingsPayload = () => {
        const result = [];
        for (const creativeId of selectedCreativeIds.value) {
            for (const gid of getFramesForCreative(creativeId)) {
                const s = frameSettingsMap[pairKey(creativeId, gid)] || {};
                const bidStrategy = s.bid_strategy || 'auto';
                result.push({
                    creative_id: creativeId,
                    gid: String(gid),
                    bid_price: bidStrategy === 'auto' ? null : (s.bid_price || null),
                    bid_strategy: bidStrategy,
                    budget_total_limit: s.budget_total_limit || null,
                    budget_daily_limit: s.budget_daily_limit || null,
                    total_impr_limit: s.total_impr_limit || null,
                    daily_impr_limit: s.daily_impr_limit || null,
                    enabled: s.enabled !== false,
                    schedule: s.schedule || null,
                });
            }
        }
        return result;
    };

    const validateBids = () => {
        const errors = {};
        for (const fs of buildFrameSettingsPayload()) {
            if (fs.enabled === false || (fs.bid_strategy || 'auto') !== 'manual') continue;
            const bidVal = Number(fs.bid_price);
            if (!Number.isFinite(bidVal) || bidVal <= 0) {
                errors.frame_settings = `Bid Price для креатива ${fs.creative_id} на экране ${fs.gid} обязателен и должен быть > 0 ₸`;
                return errors;
            }
            const maxBid = creativeBids[fs.creative_id];
            if (maxBid != null && bidVal > Number(maxBid)) {
                errors.frame_settings = `Bid Price для креатива ${fs.creative_id} на экране ${fs.gid} не должен превышать Max Bid (${maxBid} ₸)`;
                return errors;
            }
            const floorBase = Number(framesByGid.value.get(String(fs.gid))?.min_price);
            if (Number.isFinite(floorBase) && floorBase > 0) {
                const floorViewer = floorBase * viewerMultiplier.value;
                if (bidVal < floorViewer) {
                    errors.frame_settings = `Ставка на экране ${fs.gid} ниже минимальной цены площадки (${moneyFormat(floorViewer)})`;
                    return errors;
                }
            }
        }
        return errors;
    };

    const extraPayload = () => {
        const payload = {
            creative_bids: selectedCreativeIds.value.map(id => ({
                creative_id: id,
                default_bid: creativeBids[id] || null,
            })),
            frame_gids: selectedFrameGids.value,
            frame_settings: buildFrameSettingsPayload(),
        };
        if (isAdmin.value) {
            payload.campaign_frequencies = buildCampaignFrequenciesPayload();
            payload.frame_frequencies = buildFrameFrequenciesPayload();
        }
        return payload;
    };

    const extraValidate = (asDraft) => (asDraft ? {} : validateBids());

    const hydrateBindings = () => {
        isHydratingEditState.value = true;
        Object.keys(frameSettingsMap).forEach(key => delete frameSettingsMap[key]);
        Object.keys(creativeBids).forEach(key => delete creativeBids[key]);
        Object.keys(excludedPairs).forEach(key => delete excludedPairs[key]);
        Object.keys(openSchedules).forEach(key => delete openSchedules[key]);
        Object.keys(frameFrequenciesMap).forEach(key => delete frameFrequenciesMap[key]);
        frameFreqsStore = {};

        if (!isEditMode.value) {
            if (isAdmin.value) campaignFrequencies.value = buildDefaultFrequenciesForPair();
            isHydratingEditState.value = false;
            return;
        }

        for (const creative of props.campaignCreatives || []) {
            if (creative.pivot_default_bid != null) creativeBids[creative.id] = Number(creative.pivot_default_bid);
        }
        selectedFrameGids.value = (props.initialSelectedFrameGids || []).map(String).filter(Boolean);
        for (const fs of props.campaignFrameSettings || []) {
            const frameId = fs.frame_gid ?? fs.gid ?? null;
            if (frameId == null) continue;
            frameSettingsMap[`${fs.creative_id}:${frameId}`] = {
                bid_price: fs.bid_price != null ? Number(fs.bid_price) : null,
                bid_strategy: fs.bid_strategy || 'auto',
                auto_bid_current: fs.auto_bid_current != null ? Number(fs.auto_bid_current) : null,
                budget_total_limit: fs.budget_total_limit != null ? Number(fs.budget_total_limit) : null,
                budget_daily_limit: fs.budget_daily_limit != null ? Number(fs.budget_daily_limit) : null,
                total_impr_limit: fs.total_impr_limit != null ? Number(fs.total_impr_limit) : null,
                daily_impr_limit: fs.daily_impr_limit != null ? Number(fs.daily_impr_limit) : null,
                enabled: fs.enabled !== false && fs.enabled !== 0,
                schedule: fs.schedule || [],
                stats: fs.stats || null,
            };
        }
        campaignFrequencies.value = (Array.isArray(props.initialCampaignFrequencies) && props.initialCampaignFrequencies.length > 0)
            ? [...props.initialCampaignFrequencies]
            : buildDefaultFrequenciesForPair();
        frameFreqsStore = (props.initialFrameFrequenciesMap && Object.keys(props.initialFrameFrequenciesMap).length > 0)
            ? { ...props.initialFrameFrequenciesMap }
            : {};
        nextTick(() => {
            isHydratingEditState.value = false;
            editMapReady.value = true;
        });
    };

    const frequencyCells = (cells) => (Array.isArray(cells) ? cells : []).map(cell => ({
        day_of_week: Number(cell?.day_of_week),
        hour: Number(cell?.hour),
        min_interval_ms: cell?.min_interval_ms ?? null,
    }));

    const applyImportedBindings = (payload) => {
        const warnings = [];
        const availableCreativeIds = new Set((props.availableCreatives || []).concat(props.campaignCreatives || []).map(c => Number(c.id)));
        const availableFrameGids = new Set(frameRows().map(f => String(f.gid)));
        const skippedFrames = new Set();
        Object.keys(creativeBids).forEach(key => delete creativeBids[key]);
        for (const row of payload.creative_bids || []) {
            const id = Number(row.creative_id);
            if (!availableCreativeIds.has(id)) continue;
            creativeBids[id] = row.default_bid == null || row.default_bid === '' ? null : Number(row.default_bid);
        }
        const requestedGids = (payload.frame_gids || []).map(String).filter(Boolean);
        selectedFrameGids.value = requestedGids.filter((gid) => {
            if (availableFrameGids.has(gid)) return true;
            skippedFrames.add(gid);
            return false;
        });
        Object.keys(frameSettingsMap).forEach(key => delete frameSettingsMap[key]);
        for (const fs of payload.frame_settings || []) {
            const creativeId = Number(fs.creative_id);
            const gid = String(fs.gid ?? fs.frame_gid ?? '');
            if (!availableCreativeIds.has(creativeId) || !availableFrameGids.has(gid)) {
                if (gid && !availableFrameGids.has(gid)) skippedFrames.add(gid);
                continue;
            }
            frameSettingsMap[pairKey(creativeId, gid)] = {
                bid_price: fs.bid_price != null && fs.bid_price !== '' ? Number(fs.bid_price) : null,
                bid_strategy: fs.bid_strategy || 'auto',
                budget_total_limit: fs.budget_total_limit != null && fs.budget_total_limit !== '' ? Number(fs.budget_total_limit) : null,
                budget_daily_limit: fs.budget_daily_limit != null && fs.budget_daily_limit !== '' ? Number(fs.budget_daily_limit) : null,
                total_impr_limit: fs.total_impr_limit != null && fs.total_impr_limit !== '' ? Number(fs.total_impr_limit) : null,
                daily_impr_limit: fs.daily_impr_limit != null && fs.daily_impr_limit !== '' ? Number(fs.daily_impr_limit) : null,
                enabled: fs.enabled !== false && fs.enabled !== 0,
                schedule: Array.isArray(fs.schedule) ? fs.schedule : null,
            };
        }
        Object.keys(excludedPairs).forEach(key => delete excludedPairs[key]);
        Object.keys(openSchedules).forEach(key => delete openSchedules[key]);

        const hasFrequencies = Array.isArray(payload.campaign_frequencies) || Array.isArray(payload.frame_frequencies);
        if (hasFrequencies && !isAdmin.value) {
            warnings.push('Частоты из файла доступны только администратору');
        }
        if (isAdmin.value && Array.isArray(payload.campaign_frequencies)) {
            campaignFrequencies.value = frequencyCells(payload.campaign_frequencies);
        }
        if (isAdmin.value && Array.isArray(payload.frame_frequencies)) {
            Object.keys(frameFrequenciesMap).forEach(key => delete frameFrequenciesMap[key]);
            frameFreqsStore = {};
            for (const item of payload.frame_frequencies) {
                const creativeId = Number(item?.creative_id);
                const gid = String(item?.gid ?? '');
                if (!availableCreativeIds.has(creativeId) || !availableFrameGids.has(gid)) {
                    if (gid && !availableFrameGids.has(gid)) skippedFrames.add(gid);
                    continue;
                }
                const values = frequencyCells(item.values);
                const key = pairKey(creativeId, gid);
                frameFrequenciesMap[key] = values;
                frameFreqsStore[key] = values;
            }
        }
        if (skippedFrames.size) warnings.push(`Экраны не найдены: ${[...skippedFrames].join(', ')}`);
        return warnings;
    };

    const exportSelectedScreens = async () => {
        if (exportingScreens.value) return;
        exportingScreens.value = true;
        try {
            const result = await exportScreensExcel(selectedFrameGids.value, {
                campaignRtbId: props.campaign?.id || undefined,
            });
            if (!result.ok) toast.error(result.error);
        } catch {
            toast.error('Не удалось выгрузить экраны');
        } finally {
            exportingScreens.value = false;
        }
    };

    watch(selectedCreativeIds, () => {
        if (isHydratingEditState.value || isEditMode.value || selectedCreativeIds.value.length === 0) return;
        restoreExcludedPairsForSelection();
    });

    watch(selectedFrameGids, () => {
        if (isHydratingEditState.value) return;
        if (!isEditMode.value) restoreExcludedPairsForSelection();
        markDirty();
    });

    return {
        selectedFrameGids,
        exportingScreens,
        editMapReady,
        applyingDayIso,
        frameSettingsMap,
        expandedCreatives,
        creativeBids,
        frameFrequenciesMap,
        campaignFrequencies,
        selectedCreativeIdsSorted,
        showFrameMap,
        moneyColumns,
        frameSettingsColCount,
        creativeStatsSummaryById,
        setCreativeBid,
        setFrameEnabled,
        setFrameSetting,
        setFrameStringSetting,
        isFrameInRotation,
        getFrameSetting,
        getFrameStats,
        getFramesForCreative,
        canDeleteFrameBinding,
        removeFrameFromCreative,
        getCreativeName,
        getCreativeLengthSeconds,
        getFrameLabel,
        frameOtsLabel,
        formatMoney: value => moneyFormat(value, 2),
        formatMoneyPrecise: value => moneyFormat(value, 4),
        formatNumber: (val) => (val == null || val === '' ? '0' : Number(val).toLocaleString('ru-RU')),
        formatLastShowAtKz,
        formatLastShowAtKzCompact,
        toggleCreativeExpand,
        isScheduleOpen,
        getSchedule,
        setSchedule,
        toggleSchedule,
        onFrequenciesUpdated,
        onCampaignFrequenciesUpdated,
        onApplyCampaignDay,
        getAutoBidTooltip,
        extraPayload,
        extraValidate,
        hydrateBindings,
        applyImportedBindings,
        exportSelectedScreens,
        buildFrameSettingsPayload,
    };
};
